import { useState, type FormEvent } from 'react';
import { useIvrs, useSaveIvr, useDeleteTms } from '../../lib/api/hooks-tms.js';
import { TableSkeleton, Empty, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { DestinationPicker, DestinationLabel } from '../../components/telephony/DestinationPicker.js';
import { ApiClientError } from '../../lib/api/client.js';
import type { IvrMenu, Destination } from '@arcnet/contracts';

interface OptionDraft { digit: string; label: string; destination: Destination | null }

export function IvrsTab({ tenantId }: { tenantId: string }) {
  const ivrs = useIvrs(tenantId);
  const del = useDeleteTms(tenantId, 'ivrs');
  const [editing, setEditing] = useState<IvrMenu | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <>
      <div className="arc-row" style={{ marginBottom: 14 }}>
        <div>
          <h2 className="arc-h2">IVR menus</h2>
          <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            "Press 1 for sales" — a greeting and a set of keys.
          </p>
        </div>
        <div className="arc-spacer" />
        <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
          onClick={() => { setCreating((v) => !v); setEditing(null); }}>
          <Icon name={creating ? 'close' : 'plus'} size={13} />
          {creating ? 'Cancel' : 'New IVR'}
        </button>
      </div>

      {ivrs.error && <ErrorBanner error={ivrs.error} />}
      {del.error && <ErrorBanner error={del.error} />}
      {(creating || editing) && (
        <IvrEditor tenantId={tenantId} existing={editing}
          onDone={() => { setCreating(false); setEditing(null); }} />
      )}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Name</th><th>Dial</th><th>Options</th>
                <th className="arc-td-num">Timeout</th><th>On no input</th><th />
              </tr>
            </thead>
            {ivrs.isLoading ? <TableSkeleton cols={6} /> : (
              <tbody>
                {ivrs.data?.data.map((m) => (
                  <tr key={m.id}>
                    <td style={{ fontWeight: 550 }}>{m.name}</td>
                    <td className="arc-mono">{m.extension ?? <span className="arc-subtle">—</span>}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {m.options.length === 0
                          ? <span className="arc-subtle">none</span>
                          : m.options.map((o) => (
                              <Badge key={o.id} tone="muted">
                                <span className="arc-mono" style={{ fontSize: 11 }}>{o.digit}</span>
                                {o.label ? ` ${o.label}` : ''}
                              </Badge>
                            ))}
                      </div>
                    </td>
                    <td className="arc-td-num">{m.timeout_sec}s</td>
                    <td><DestinationLabel tenantId={tenantId} destination={m.timeout_destination} /></td>
                    <td className="arc-td-right">
                      <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                          onClick={() => { setEditing(m); setCreating(false); }}>Edit</button>
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                          onClick={() => { if (confirm(`Delete IVR '${m.name}'?`)) del.mutate(m.id); }}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {!ivrs.isLoading && ivrs.data?.data.length === 0 && (
          <Empty icon="ivr" title="No IVR menus"
            hint="An IVR answers, plays a greeting and routes on the key pressed." />
        )}
      </div>
    </>
  );
}

function IvrEditor({ tenantId, existing, onDone }: {
  tenantId: string; existing: IvrMenu | null; onDone: () => void;
}) {
  const save = useSaveIvr(tenantId);
  const [name, setName] = useState(existing?.name ?? '');
  const [ext, setExt] = useState(existing?.extension ?? '');
  const [timeoutSec, setTimeoutSec] = useState(String(existing?.timeout_sec ?? 5));
  const [directDial, setDirectDial] = useState(existing?.direct_dial ?? false);
  const [onTimeout, setOnTimeout] = useState<Destination | null>(existing?.timeout_destination ?? null);
  const [options, setOptions] = useState<OptionDraft[]>(
    existing?.options.map((o) => ({
      digit: o.digit, label: o.label ?? '', destination: o.destination,
    })) ?? [{ digit: '1', label: '', destination: null }],
  );

  const setOpt = (i: number, patch: Partial<OptionDraft>) =>
    setOptions((prev) => prev.map((o, j) => j === i ? { ...o, ...patch } : o));

  const usedDigits = new Set(options.map((o) => o.digit));
  const duplicate = usedDigits.size !== options.length;
  const err = save.error instanceof ApiClientError ? save.error : null;

  return (
    <form
      className="arc-card arc-card-body" style={{ marginBottom: 14 }}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        save.mutate({
          ...(existing ? { id: existing.id } : {}),
          name, extension: ext || null, timeout_sec: Number(timeoutSec),
          direct_dial: directDial, timeout_destination: onTimeout,
          options: options
            .filter((o) => o.destination !== null)
            .map((o) => ({
              digit: o.digit,
              ...(o.label ? { label: o.label } : {}),
              destination: o.destination,
            })),
        }, { onSuccess: onDone });
      }}
    >
      <h3 className="arc-h2" style={{ marginBottom: 12 }}>
        {existing ? `Edit ${existing.name}` : 'New IVR menu'}
      </h3>
      {err && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 12 }} role="alert">
          <Icon name="warning" size={14} /><span>{err.message}</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 110px 120px', gap: 12, marginBottom: 14 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="iv-name">Name</label>
          <input id="iv-name" className="arc-input" required value={name}
            onChange={(e) => setName(e.target.value)} placeholder="Main menu" />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="iv-ext">Dial code</label>
          <input id="iv-ext" className="arc-input" value={ext}
            onChange={(e) => setExt(e.target.value)} placeholder="400" />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="iv-to">Wait for key (s)</label>
          <input id="iv-to" className="arc-input" inputMode="numeric" value={timeoutSec}
            onChange={(e) => setTimeoutSec(e.target.value)} />
        </div>
      </div>

      <div className="arc-field">
        <div className="arc-row" style={{ marginBottom: 6 }}>
          <label className="arc-label" style={{ margin: 0 }}>Key options</label>
          <div className="arc-spacer" />
          <button type="button" className="arc-btn arc-btn-sm"
            onClick={() => setOptions((p) => [...p, { digit: '', label: '', destination: null }])}>
            <Icon name="plus" size={12} /> Add key
          </button>
        </div>

        {duplicate && (
          <div className="arc-banner arc-banner-warn" style={{ marginBottom: 8 }}>
            <Icon name="warning" size={14} />
            <span>Two options share a key. Only one would ever be reachable.</span>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {options.map((o, i) => (
            <div key={i} className="arc-row" style={{ gap: 8 }}>
              <input
                className="arc-input arc-mono" style={{ width: 52, textAlign: 'center' }}
                value={o.digit} maxLength={1}
                onChange={(e) => setOpt(i, { digit: e.target.value })}
                aria-label={`Key for option ${i + 1}`}
              />
              <input
                className="arc-input" style={{ maxWidth: 190 }} placeholder="Label (optional)"
                value={o.label} onChange={(e) => setOpt(i, { label: e.target.value })}
                aria-label={`Label for option ${i + 1}`}
              />
              <div style={{ flex: 1 }}>
                <DestinationPicker tenantId={tenantId} value={o.destination}
                  onChange={(d) => setOpt(i, { destination: d })} allowNone />
              </div>
              <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon"
                onClick={() => setOptions((p) => p.filter((_, j) => j !== i))}
                aria-label={`Remove option ${i + 1}`}>
                <Icon name="trash" size={13} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="arc-row" style={{ marginBottom: 12, gap: 8 }}>
        <input id="iv-dd" type="checkbox" checked={directDial}
          onChange={(e) => setDirectDial(e.target.checked)} />
        <label htmlFor="iv-dd" style={{ fontSize: 12.5 }}>
          Allow callers to dial an extension directly
        </label>
      </div>

      <div className="arc-field">
        <label className="arc-label">If nothing is pressed</label>
        <DestinationPicker tenantId={tenantId} value={onTimeout} onChange={setOnTimeout} allowNone />
      </div>

      <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending || duplicate}>
        {save.isPending ? 'Saving…' : existing ? 'Save changes' : 'Create IVR'}
      </button>
    </form>
  );
}
